"use client";

import React, { useEffect, useRef } from "react";
import Image from "next/image";
import { motion } from "motion/react";
import { gsap } from "gsap";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { testimonialsData } from "@/data/testimonials";
import styles from "./Testimonials.module.css";

if (typeof window !== "undefined") {
  gsap.registerPlugin();
}

export function TestimonialsSection() {
  const trackRef = useRef<HTMLDivElement>(null);
  const tweenRef = useRef<gsap.core.Tween | null>(null);

  // Duplicate the items once to guarantee a seamless structural wrap transition edge
  const continuousSlides = [...testimonialsData, ...testimonialsData];

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const ctx = gsap.context(() => {
      const totalWidth = track.scrollWidth / 2;

      // Setup continuous auto-scrolling matrix
      tweenRef.current = gsap.to(track, {
        x: `-=${totalWidth}`,
        duration: 25,
        ease: "none",
        repeat: -1,
        modifiers: {
          x: gsap.utils.unitize((x) => parseFloat(x) % totalWidth)
        }
      });
    });

    return () => ctx.revert();
  }, []);

  // Controls pause/play during interaction states cleanly
  const handleMouseEnter = () => tweenRef.current?.pause();
  const handleMouseLeave = () => tweenRef.current?.play();

  const handlePrev = () => {
    if (!tweenRef.current) return;
    gsap.to(tweenRef.current, { timeScale: -3, duration: 0.2, ease: "power1.out" });
    setTimeout(() => {
      gsap.to(tweenRef.current, { timeScale: 1, duration: 0.8, ease: "power1.inOut" });
    }, 400);
  };

  const handleNext = () => {
    if (!tweenRef.current) return;
    gsap.to(tweenRef.current, { timeScale: 3, duration: 0.2, ease: "power1.out" });
    setTimeout(() => {
      gsap.to(tweenRef.current, { timeScale: 1, duration: 0.8, ease: "power1.inOut" });
    }, 400);
  };

  return (
    <section className={styles.sectionContainer}>
      
      {/* Header Area */}
      <div className={styles.headerArea}>
        <span className={styles.subTag}>Testimonials</span>
        <h2 className={styles.mainTitle}>
          <span>Voices of</span>
          <span className={styles.titleOutline}>Global Partners</span>
        </h2>
      </div>

      {/* Main Track Viewport Mapped to GSAP Loop Engine */}
      <div 
        className={styles.trackViewport}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div ref={trackRef} className={styles.cardsTrack}>
          {continuousSlides.map((item, index) => (
            <div className={styles.cardBox} key={`${item.id}-${index}`}>
              
              {/* Framed Avatar */}
              <div className={styles.avatarRing}>
                <Image
                  src={item.authorImage}
                  alt={item.role}
                  fill
                  sizes="70px"
                  priority={index < 4}
                />
              </div>

              {/* Quote Statement */}
              <p className={styles.quoteText}>
                "{item.quote}"
              </p>

              {/* Author Info */}
              <div className="flex flex-col items-center">
                <span className={styles.roleTag}>{item.role}</span>
                <div className={styles.starGroup}>
                  {Array.from({ length: item.rating }).map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-current" />
                  ))}
                </div>
              </div>

            </div>
          ))}
        </div>
      </div>

      {/* Minimalistic Control Toggles */}
      <div className={styles.controlsRow}>
        <motion.button 
          whileTap={{ scale: 0.95 }}
          onClick={handlePrev} 
          className={styles.navButton}
          aria-label="Previous testimonials"
        >
          <ChevronLeft className="h-5 w-5" />
        </motion.button>
        
        <motion.button 
          whileTap={{ scale: 0.95 }}
          onClick={handleNext} 
          className={styles.navButton}
          aria-label="Next testimonials"
        >
          <ChevronRight className="h-5 w-5" />
        </motion.button>
      </div>

    </section>
  );
}